import axios from 'axios';
import { getApiErrorMessage } from '../utils/apiErrors';

const configuredApiUrl = process.env.REACT_APP_API_URL;
const isInvalidProductionApiUrl = process.env.NODE_ENV === 'production' && (!configuredApiUrl || !configuredApiUrl.startsWith('https://'));
if (isInvalidProductionApiUrl) {
  // eslint-disable-next-line no-console
  console.warn('REACT_APP_API_URL must be configured with the HTTPS backend URL for production builds.');
}

export const API_BASE_URL = configuredApiUrl || 'http://localhost:3000/api';
export const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');
export const SESSION_HINT_KEY = 'clarityHasSession';

const storageGet = (storage, key) => {
  try { return storage?.getItem?.(key); } catch { return null; }
};

const storageSet = (storage, key, value) => {
  try { storage?.setItem?.(key, value); } catch { /* ignore unavailable storage */ }
};

const storageRemove = (storage, key) => {
  try { storage?.removeItem?.(key); } catch { /* ignore unavailable storage */ }
};

export const hasSessionHint = () => (
  storageGet(localStorage, SESSION_HINT_KEY) === 'true'
  || storageGet(sessionStorage, SESSION_HINT_KEY) === 'true'
);

export const clearSessionHints = () => {
  storageRemove(localStorage, SESSION_HINT_KEY);
  storageRemove(sessionStorage, SESSION_HINT_KEY);
};

export const setSessionHint = (remember = true) => {
  clearSessionHints();
  storageSet(remember === false ? sessionStorage : localStorage, SESSION_HINT_KEY, 'true');
};

export const API_CONFIGURATION_ERROR = isInvalidProductionApiUrl
  ? 'Configuração da API inválida. Configure REACT_APP_API_URL com a URL HTTPS do backend.'
  : '';

let memoryAccessToken = null;

export const setAccessToken = (token) => {
  memoryAccessToken = token || null;
};

export const getAccessToken = () => memoryAccessToken;

export const clearAccessToken = () => {
  memoryAccessToken = null;
  delete api.defaults.headers.common.Authorization;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  withCredentials: true,
});

api.interceptors.request.use(
  (config) => {
    if (API_CONFIGURATION_ERROR) {
      return Promise.reject(new Error(API_CONFIGURATION_ERROR));
    }
    config.headers['X-Client-Platform'] = 'web';
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (typeof config.headers?.delete === 'function') config.headers.delete('Content-Type');
      else delete config.headers['Content-Type'];
    }
    const token = getAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue = [];

const PUBLIC_AUTH_ROUTES = ['/auth/login', '/auth/register', '/auth/forgot-password', '/auth/reset-password'];
const isPublicAuthRoute = (url = '') => PUBLIC_AUTH_ROUTES.some((route) => String(url).includes(route));

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    if (error.response?.data && typeof error.response.data === 'object') {
      error.response.data.error = getApiErrorMessage(error, error.response.data.error || error.message);
    } else if (error.response) {
      error.response.data = { error: getApiErrorMessage(error, error.message) };
    }

    if (error.response?.status === 429 && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('clarity:rate-limit', {
        detail: {
          message: error.response?.data?.error || getApiErrorMessage(error),
        },
      }));
    }

    if (error.response?.status === 401 && !originalRequest._retry && !isPublicAuthRoute(originalRequest.url)) {
      if (originalRequest.url?.includes('/auth/refresh')) {
        clearAccessToken();
        clearSessionHints();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true, headers: { 'X-Client-Platform': 'web' } }
        );
        setAccessToken(data.accessToken);
        api.defaults.headers.common.Authorization = `Bearer ${data.accessToken}`;
        processQueue(null, data.accessToken);
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAccessToken();
        clearSessionHints();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
